import {
  Button,
  Modal,
  type ModalProps,
  useToastContext,
} from '@payaca/ambrosia';
import DropdownField from '@payaca/components/dropdownField/DropdownField';
import {
  ListedServicePlan,
  PublicHydratedServicePlanDiscountCode,
  PublicServicePlan,
  PublicServicePlanDiscountCode,
} from '@payaca/types/service-plans';
import * as clipboard from 'clipboard-polyfill/text';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import './CopyServicePlanPublicLinkModal.sass';

interface IProps extends Omit<ModalProps, 'title'> {
  servicePlanPublicId: ListedServicePlan['publicId'];
  servicePlanDiscountCodes: PublicHydratedServicePlanDiscountCode[];
}

export const useCopyServicePlanPublicLinkToClipboard = () => {
  const { pushInfo } = useToastContext();

  return useCallback(
    async (
      servicePlanPublicId: PublicServicePlan['publicId'],
      discountCodeClientCode?: PublicServicePlanDiscountCode['code']
    ) => {
      let publicLink = new URL(
        `${
          import.meta.env.VITE_CLIENT_APP_URL
        }/service-plan/${servicePlanPublicId}`
      );

      if (discountCodeClientCode) {
        const urlParams = new URLSearchParams({
          discount_code: discountCodeClientCode,
        });

        publicLink = new URL(publicLink.href + '?' + urlParams.toString());
      }

      await clipboard.writeText(publicLink.href);

      pushInfo(
        <>
          <strong>Copied!</strong> You can now paste your link into any
          e-mails/your social media accounts
        </>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'pushInfo'
    []
  );
};

const CopyServicePlanPublicLinkModal: FC<IProps> = (props) => {
  const {
    servicePlanDiscountCodes,
    className,
    isOpen,
    onClose,
    servicePlanPublicId,
    ...rest
  } = props;

  const [selectedDiscountCodeClientId, setSelectedDiscountCodeClientId] =
    useState<PublicHydratedServicePlanDiscountCode['code'] | undefined>();
  const copyServicePlanPublicLinkToClipboard =
    useCopyServicePlanPublicLinkToClipboard();

  const discountCodeOptions = useMemo(
    () =>
      servicePlanDiscountCodes.map((i) => ({
        label: i.name,
        value: i.code,
      })),
    [servicePlanDiscountCodes]
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedDiscountCodeClientId(undefined);
    }
  }, [isOpen]);

  const handleCopyLink = async () => {
    await copyServicePlanPublicLinkToClipboard(
      servicePlanPublicId,
      selectedDiscountCodeClientId
    );
    onClose?.();
  };

  return (
    <Modal
      className={className}
      isOpen={isOpen}
      title="Copy service plan public link"
      onClose={onClose}
      {...rest}
    >
      <Modal.Body>
        {!!servicePlanDiscountCodes?.length && (
          <DropdownField
            label="Apply a discount"
            value={selectedDiscountCodeClientId || ''}
            name="discountCodeId"
            options={discountCodeOptions}
            onChange={(value) => {
              setSelectedDiscountCodeClientId(value.discountCodeId);
            }}
            emptyText="Choose a discount"
            additionalSelectProps={{
              displayEmpty: true,
            }}
            hasBoxShadow={false}
          />
        )}

        <Button
          className="copy-service-plan-public-link-modal__btn w-full"
          size="md"
          onClick={handleCopyLink}
        >
          Copy public link
        </Button>
      </Modal.Body>
    </Modal>
  );
};

export default CopyServicePlanPublicLinkModal;
